import LogicFlow from '@logicflow/core';
import { useCallback } from 'react';

interface UseDataMapControlProps {
  lfRef: React.RefObject<LogicFlow | null>;
  canvasRef: React.RefObject<HTMLDivElement | null>;
  setSavedViewState: (state: any) => void;
  savedViewState?: any;
}

/**
 * 数据映射编辑器的画布控制钩子
 * 提供缩放、重置视图、保存/恢复视图状态等功能
 */
const useDataMapControl = ({ lfRef, canvasRef, setSavedViewState, savedViewState }: UseDataMapControlProps) => {
  /**
   * 放大画布
   * 每次放大1.2倍，最大放大到3倍
   */
  const handleZoomIn = useCallback(() => {
    if (!lfRef?.current) return;
    
    try {
      // 获取当前缩放比例
      const transform = lfRef.current.getTransform() as any;
      const currentScale = transform?.SCALE_X || 1;

      // 放大1.2倍，但不超过3倍
      const newScale = Math.min(currentScale * 1.2, 3);
      lfRef.current.zoom(newScale);
    } catch (error) {
      console.error('放大画布失败:', error);
    }
  }, [lfRef]);

  /**
   * 缩小画布
   * 每次缩小1.2倍，最小缩小到0.1倍
   */
  const handleZoomOut = useCallback(() => {
    if (!lfRef?.current) return;
    
    try {
      // 获取当前缩放比例
      const transform = lfRef.current.getTransform() as any;
      const currentScale = transform?.SCALE_X || 1;

      // 缩小1.2倍，但不小于0.1倍
      const newScale = Math.max(currentScale / 1.2, 0.1);
      lfRef.current.zoom(newScale);
    } catch (error) {
      console.error('缩小画布失败:', error);
    }
  }, [lfRef]);

  /**
   * 重置视图
   * 重置缩放比例和平移位置，并将所有节点居中显示
   */
  const handleResetView = useCallback(() => {
    if (!lfRef.current) return;

    try {
      // 重置缩放和平移
      lfRef.current.resetZoom();
      lfRef.current.resetTranslate();

      // 获取图数据
      const graphData = lfRef.current.getGraphData() as {
        nodes: any[];
        edges: any[];
      };
      
      // 如果有节点，计算中心点并居中显示
      if (graphData.nodes && graphData.nodes.length > 0) {
        let totalX = 0, totalY = 0;
        const nodeCount = graphData.nodes.length;
        
        // 计算所有节点的平均位置
        graphData.nodes.forEach(node => {
          totalX += node.x || 0;
          totalY += node.y || 0;
        });
        
        const centerX = totalX / nodeCount;
        const centerY = totalY / nodeCount;

        // 获取画布尺寸
        const canvas = canvasRef.current;
        if (canvas){
          const canvasWidth = canvas.clientWidth || 800;
          const canvasHeight = canvas.clientHeight || 600;

          // 计算平移量，使节点中心与画布中心对齐
          const translateX = canvasWidth / 2 - centerX;
          const translateY = canvasHeight / 2 - centerY;

          lfRef.current.translate(translateX, translateY);
        }
      }
    } catch (error) {
      console.error('重置视图失败:', error);
    }
  }, [lfRef, canvasRef]);

  /**
   * 保存当前视图状态
   * 保存当前的缩放比例和平移位置
   */
  const handleSaveViewState = useCallback(() => {
    if (!lfRef.current) return;

    try {
      const transform = lfRef.current.getTransform();
      setSavedViewState(transform);
      console.log('保存视图状态:', transform);
    } catch (error) {
      console.error('保存视图状态失败:', error);
    }
  }, [lfRef, setSavedViewState]);

  /**
   * 恢复视图状态
   * 恢复之前保存的缩放比例和平移位置
   */
  const handleRestoreViewState = useCallback(() => {
    if (!lfRef.current) return; 
    
    try {
      // 检查是否有保存的视图状态
      if (!savedViewState) {
        console.warn('没有保存的视图状态');
        return;
      }
      
      // 恢复缩放和平移
      lfRef.current.zoom(savedViewState?.scale || 1);
      lfRef.current.translate(
        savedViewState?.translateX || 0,
        savedViewState?.translateY || 0
      );
    } catch (error) {
      console.warn('还原视图状态失败:', error);
    }
  }, [lfRef, savedViewState]);

  /**
   * 平移画布
   * @param dx 水平平移量
   * @param dy 垂直平移量
   */
  const handleTranslate = useCallback((dx: number, dy: number) => {
    if (!lfRef.current) return;

    try {
      lfRef.current.translate(dx, dy);
    } catch (error) {
      console.error('平移画布失败:', error);
    }
  }, [lfRef]);

  return {
    handleZoomIn,
    handleZoomOut,
    handleResetView,
    handleSaveViewState,
    handleRestoreViewState,
    handleTranslate,
  };
};

export default useDataMapControl;
